import {
    StyledAnnualKwh,
    StyledComponentCardWrapper,
    StyledComponentsQuoteWrapper,
    StyledTitle
} from '@/features/quote/components/components-quote/style.ts'
import {useTranslation} from 'react-i18next'
import {QuoteCalculationsType, QuoteType} from '@/features/quote/types.ts'
import {SolarPanelIcon} from '@/features/quote/components/operation-system/icons/SolarPanelIcon.tsx'
import {InverterIcon} from '@/features/quote/components/operation-system/icons/InverterIcon.tsx'
import {BatteryIcon} from '@/features/quote/components/operation-system/icons/BatteryIcon.tsx'
import {OptimizerIcon} from '@/features/quote/components/operation-system/icons/OptimizerIcon.tsx'
import {ReactNode} from 'react'
import {formatValues} from '@/features/project-details/helpers/helpers'
import {FixingIcon} from '../operation-system/icons/FixingIcon'
import {AccessoriesIcon} from '../operation-system/icons/AccessoriesIcon'
import {ComponentCard} from './ComponentCard'
import {ComponentCategoriesE} from '@/features/catalog-components/services/catalogComponents.schema'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ExtraIcon} from '@/features/quote/components/operation-system/icons/ExtraIcon.tsx'
import {EuroIcon} from '@/features/quote/components/operation-system/icons/EuroIcon.tsx'

interface ProductType {
    id: string
    name: string
    attributes: Array<{
        id: string
        createdAt: string
        updatedAt: string
        deletedAt: string | null
        name: string
        slug: string
        pivot: {
            pivot_product_id: string
            pivot_attribute_id: string
            pivot_value_string: string | null
            pivot_value_number: number | null
        }
    }>
    category: {
        slug: string
    }
    finalLinePrice: number
    unitPrice: number
    quantity: number
    slug: string
}
interface GroupType {
    title: string
    image: ReactNode
    products: ProductType[]
}

export const ComponentsQuote = ({
    quoteData,
    quoteCalculationsData
}: {
    quoteData: QuoteType
    quoteCalculationsData: QuoteCalculationsType
}) => {
    const {t} = useTranslation()

    const titleToRender = (category: string) => {
        switch (category) {
            case ComponentCategoriesE.Panel:
                return {
                    title: t('quote:components:panel'),
                    image: (
                        <Flexbox gap={2} align={'center'}>
                            <SolarPanelIcon />{' '}
                            <StyledAnnualKwh>
                                {quoteCalculationsData.systemSize} {t('quote:components:annual_kwh')}
                            </StyledAnnualKwh>
                        </Flexbox>
                    )
                }
            case ComponentCategoriesE.Inverter:
                return {
                    title: t('quote:components:inverter'),
                    image: <InverterIcon />
                }

            case ComponentCategoriesE.Battery:
            case ComponentCategoriesE.CombinerBox:
                return {
                    title: t('quote:components:battery'),
                    image: <BatteryIcon />
                }
            case ComponentCategoriesE.Cables:
                return {
                    title: t('quote:components:accessories_panels'),
                    image: <AccessoriesIcon />
                }
            case ComponentCategoriesE.WallBattery:
            case ComponentCategoriesE.Optimizer:
            case ComponentCategoriesE.Connectors:
                return {
                    title: t('quote:components:accessories_panels'),
                    image: <BatteryIcon />
                }
            case ComponentCategoriesE.PowerDistributionPanelsAC:
            case ComponentCategoriesE.PowerDistributionPanelsDC:
            case ComponentCategoriesE.PowerDistributionPanelsACDC:
            case ComponentCategoriesE.PowerDistributionPanelsOther:
                return {
                    title: t('quote:components:electrical_material'),
                    image: <OptimizerIcon />
                }
            case ComponentCategoriesE.Ballast:
            case ComponentCategoriesE.Rail:
            case ComponentCategoriesE.Triangles:
            case ComponentCategoriesE.ScrewAndBracket:
            case ComponentCategoriesE.CentralEndClamp:
            case ComponentCategoriesE.EndClamp:
            case ComponentCategoriesE.RailClamp:
            case ComponentCategoriesE.Splice:
                return {
                    title: t('quote:components:fixings'),
                    image: <FixingIcon />
                }
            case ComponentCategoriesE.Other:
                return {
                    title: t('tabs:additional_costs'),
                    image: <EuroIcon />
                }
            default:
                return {
                    title: t('tabs:other'),
                    image: <ExtraIcon />
                }
        }
    }
    const renderProductDescription = (product: ProductType, quoteCalculationsData: QuoteCalculationsType) => {
        const quantity = product.quantity
        switch (product.category.slug) {
            case ComponentCategoriesE.Inverter:
                return (
                    <Flexbox key={product.id} direction={'column'} gap={2}>
                        <div>
                            <p>
                                {t('commons:model')}: {product.name}
                            </p>
                            <p>
                                {t('commons:power')}:{' '}
                                {formatValues(
                                    product?.attributes?.find(attr => attr.name === 'power')?.pivot.pivot_value_number
                                )}
                                kW
                            </p>
                            <p>
                                {t('commons:description')}:{' '}
                                {
                                    product?.attributes?.find(attr => attr.name === 'description')?.pivot
                                        .pivot_value_string
                                }
                            </p>
                        </div>
                        <p>
                            {t('commons:quantity')}: {quantity}
                        </p>
                    </Flexbox>
                )
            case ComponentCategoriesE.Battery:
                return (
                    <Flexbox key={product.id} direction={'column'} gap={2}>
                        <div>
                            <p>
                                {t('commons:model')}: {product.name}
                            </p>
                            <p>
                                {t('commons:power')}: {quoteCalculationsData.effectiveAccumulation}
                                {t('commons:kilowatt')}
                            </p>
                            <p>
                                {t('commons:description')}:{' '}
                                {
                                    product?.attributes?.find(attr => attr.name === 'description')?.pivot
                                        .pivot_value_string
                                }
                            </p>
                        </div>
                        <p>
                            {t('commons:quantity')}: {quantity}
                        </p>
                    </Flexbox>
                )
            case ComponentCategoriesE.Panel:
                console.log(product)
                return (
                    <Flexbox key={product.id} direction={'column'} gap={2}>
                        <div>
                            <p>
                                {t('commons:model')}: {product.name}
                            </p>
                            <p>
                                {t('commons:power')}:{' '}
                                {Number(
                                    product?.attributes?.find(attr => attr.name === 'power')?.pivot.pivot_value_number
                                ).toFixed(0)}
                                {t('commons:watt')} ({t('quote:components:total_power')}{' '}
                                {(
                                    (Number(
                                        product?.attributes?.find(attr => attr.name === 'power')?.pivot
                                            .pivot_value_number
                                    ) /
                                        1000) *
                                    quantity
                                ).toFixed(2)}
                                {t('commons:kilowatt')})
                            </p>
                            <p>
                                {t('commons:description')}:{' '}
                                {
                                    product?.attributes?.find(attr => attr.name === 'description')?.pivot
                                        .pivot_value_string
                                }
                                ({product.slug})
                            </p>
                        </div>
                        <p>
                            {t('commons:quantity')}: {quantity}
                        </p>
                    </Flexbox>
                )
            case ComponentCategoriesE.Cables:
                return (
                    <p key={product.id}>
                        {quantity}m{' '}
                        {product?.attributes?.find(attr => attr.name === 'description')?.pivot.pivot_value_string}
                    </p>
                )
            case ComponentCategoriesE.Other:
                return (
                    <Flexbox key={product.id} fullWidth gap={1}>
                        <p>
                            •{' '}
                            {product?.attributes?.find(attr => attr.name === 'description')?.pivot.pivot_value_string ??
                                product.name}
                        </p>
                        <p>{'='}</p>
                        <p>{`${formatValues(product?.finalLinePrice)}€`}</p>
                    </Flexbox>
                )
            default:
                return (
                    <p key={product.id}>
                        {quantity} x{' '}
                        {product?.attributes?.find(attr => attr.name === 'description')?.pivot.pivot_value_string ??
                            product.name}
                    </p>
                )
        }
    }

    const orderedTitles = [
        'Pannelli fotovoltaici',
        'Inverter',
        'Batteria di accumulo',
        'Fissaggi',
        'Quadri elettrici',
        'Accessori pannelli',
        'Altro'
    ]

    const groupedProducts = quoteData?.products?.reduce<{[key: string]: GroupType}>((groups, product) => {
        console.log(product)
        const {title, image} = titleToRender(product.product.category.slug)
        if (!groups[title]) {
            groups[title] = {
                title,
                image,
                products: []
            }
        }
        groups[title].products.push({
            ...product,
            category: product.product.category,
            quantity: Number(product.quantity),
            finalLinePrice: Number(product.finalLinePrice),
            unitPrice: Number(product.unitPrice),
            slug: product.product.slug,
            name: product.product.name,
            attributes: product?.product?.attributes
        })
        return groups
    }, {})

    // Sort the groups according to the ordered titles
    const sortedGroups = Object.fromEntries(
        orderedTitles.filter(title => groupedProducts?.[title]).map(title => [title, groupedProducts[title]])
    )

    return (
        <>
            <StyledComponentsQuoteWrapper direction={'column'} gap={4}>
                <StyledTitle>{t('quote:components:title')}</StyledTitle>

                <StyledComponentCardWrapper>
                    {Object.values(sortedGroups).map(group => {
                        const {title, image, products} = group

                        return (
                            <ComponentCard
                                key={title}
                                title={title}
                                image={image}
                                description={products.map(product =>
                                    renderProductDescription(product, quoteCalculationsData)
                                )}
                            />
                        )
                    })}
                </StyledComponentCardWrapper>
            </StyledComponentsQuoteWrapper>
        </>
    )
}
